import React, { useState, useEffect } from 'react';
import './ScreenerTable.css';
import { fetchScreenerData } from '../../services/screenerApi.ts';

export interface ScreenerStock {
  id: string;
  stockName: string;
  symbol: string;
  exchange: string;
  timeframe: string;
  capturedPrice: string;
  capturedOn: string;
  chartUrl: string;
  maxMove: number;
  chartConfig?: ChartConfig;
}

export interface ChartConfig {
  capturedPriceLine?: boolean;
  capturedPriceRectangle?: boolean;
}

export interface ColumnConfig {
  key: keyof ScreenerStock;
  label: string;
  visible: boolean;
}

export interface PaginationResponse {
  data: ScreenerStock[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    pageSize: number;
  };
}

export interface ScreenerTableProps {
  apiEndpoint: string;
  queryParams?: Record<string, string>;
  columns?: ColumnConfig[];
  initialPageSize?: number;
  viewChartButtonEnable?: boolean;
  onViewChart?: (stock: ScreenerStock) => void;
}

const defaultColumns: ColumnConfig[] = [
  { key: 'stockName', label: 'Stock Name', visible: true },
  { key: 'symbol', label: 'Symbol', visible: true },
  { key: 'exchange', label: 'Exch', visible: true },
  { key: 'timeframe', label: 'Timeframe', visible: true },
  { key: 'capturedPrice', label: 'Captured Price', visible: true },
  { key: 'capturedOn', label: 'Captured On', visible: true },
];

const ScreenerTable: React.FC<ScreenerTableProps> = ({
  apiEndpoint,
  queryParams = {},
  columns = defaultColumns,
  initialPageSize = 10,
  onViewChart,
  viewChartButtonEnable = false,
}) => {
  const [data, setData] = useState<ScreenerStock[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize, setPageSize] = useState(initialPageSize);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Build query string
        const params = new URLSearchParams({
          page: currentPage.toString(),
          pageSize: pageSize.toString(),
          ...queryParams,
        });

        console.log('Fetching data...', params.toString());

        // Call the API service
        const response = await fetchScreenerData(apiEndpoint, params.toString());

        console.log('Data received:', response);

        setData(response.data);
        setTotalPages(response.pagination.totalPages);
        setTotalItems(response.pagination.totalItems);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, apiEndpoint]);


  const handleViewChart = (stock: ScreenerStock) => {
    if (onViewChart) {
      onViewChart(stock);
    } else {
      // Default behavior: open in new tab
      window.open(stock.chartUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setPageSize(Number(event.target.value));
    setCurrentPage(1); // Reset to first page
  };

  const visibleColumns = columns.filter((col) => col.visible);

  // Render cell content based on column key
  const renderCellContent = (row: ScreenerStock, columnKey: keyof ScreenerStock) => {
    switch (columnKey) {
      case 'stockName':
        return row.stockName;
      case 'symbol':
        return row.symbol;
      case 'exchange':
        return row.exchange;
      case 'timeframe':
        return row.timeframe;
      case 'capturedPrice':
        return row.capturedPrice;
      case 'capturedOn':
        return row.capturedOn;
      case 'maxMove':
        return (
          <span className={`max-move ${Number(row.maxMove) >= 0 ? 'profit' : 'loss'}`}>
            {Number(row.maxMove) >= 0 ? '' : ''}{Math.abs(row.maxMove)}%
          </span>
        );
      case 'chartUrl':
        return row.chartUrl;
      case 'chartConfig':
        return null; // Don't render objects directly
      default:
        return null;
    }
  };

  // Generate page numbers
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  return (
    <div className="screener-table-wrapper">
      {/* Desktop Table View */}
      <div className="screener-table-container-desktop">
        {loading ? (
          <div className="screener-table-loading">
            <div className="screener-loading-spinner"></div>
            <p>Loading data...</p>
          </div>
        ) : (
          <table className="screener-table">
            <thead>
              <tr>
                {visibleColumns.map((col) => (
                  <th key={col.key}>{col.label}</th>
                ))}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.length === 0 ? (
                <tr>
                  <td colSpan={visibleColumns.length + 1} className="no-data">
                    No data available
                  </td>
                </tr>
              ) : (
                data.map((row) => (
                  <tr key={row.id}>
                    {visibleColumns.map((col) => (
                      <td key={`${row.id}-${col.key}`}>
                        {renderCellContent(row, col.key)}
                      </td>
                    ))}
                    <td>
                      {(row?.chartUrl && viewChartButtonEnable) ? (<button
                        className="view-chart-btn"
                        onClick={() => handleViewChart(row)}
                      >
                        View Chart
                      </button>) : <></>}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Mobile Card View */}
      <div className="screener-table-container-mobile">
        {loading ? (
          <div className="screener-table-loading">
            <div className="screener-loading-spinner"></div>
            <p>Loading data...</p>
          </div>
        ) : data.length === 0 ? (
          <div className="no-data">No data available</div>
        ) : (
          data.map((row) => (
            <div key={row.id} className="screener-mobile-card">
              {visibleColumns.map((col) => (
                <div key={`${row.id}-${col.key}`} className="screener-mobile-row">
                  <div className="screener-mobile-label">{col.label}</div>
                  <div className="screener-mobile-value">
                    {renderCellContent(row, col.key)}
                  </div>
                </div>
              ))} {(row?.chartUrl && viewChartButtonEnable) ? (<div className="screener-mobile-action">
                <button
                  className="view-chart-btn"
                  onClick={() => handleViewChart(row)}
                >
                  View Chart
                </button>
              </div>) : <></>}
            </div>
          ))
        )}
      </div>

      {/* Pagination Controls */}
      {!loading && data.length > 0 && (
        <div className="screener-pagination">
          <div className="pagination-info">
            Showing {(currentPage - 1) * pageSize + 1} to{' '}
            {Math.min(currentPage * pageSize, totalItems)} of {totalItems} entries
          </div>

          <div className="pagination-controls">
            {/* Page Size Selector */}
            <div className="page-size-selector">
              <label htmlFor="pageSize">Rows per page:</label>
              <select
                id="pageSize"
                value={pageSize}
                onChange={handlePageSizeChange}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>

            {/* Page Navigation */}
            <div className="page-navigation">
              <button
                className="page-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>

              <div className="page-numbers">
                {getPageNumbers().map((page, index) =>
                  typeof page === 'number' ? (
                    <button
                      key={index}
                      className={`page-number ${currentPage === page ? 'active' : ''
                        }`}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </button>
                  ) : (
                    <span key={index} className="page-ellipsis">
                      {page}
                    </span>
                  )
                )}
              </div>

              <button
                className="page-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScreenerTable;
